import { Typography } from "@material-tailwind/react";
import { Tooltip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { utils } from "ethers";
import moment from "moment";
import { useAccount, useNetwork } from "wagmi";
import { getCdnUrl } from "../../lib/getCdnUrl";
import useGetOwnedNftCounts from "../../services/useGetOwnedNftCounts";
import { setModal } from "../../redux/counterSlice";
import { useDispatch } from "react-redux";
import { useChainModal, useConnectModal } from "@rainbow-me/rainbowkit";
import { getBalanceAlert } from "../balanceAlert";

export const GlobalOffersTable = ({
  rows,
  totalPages,
  totalCount,
  currentPage,
  setCurrentPage,
  loading,
  pageSize = 10,
  reset,
}: {
  rows: any[];
  totalPages: number;
  totalCount: number;
  currentPage: number;
  setCurrentPage: any;
  loading: boolean;
  pageSize?: number;
  reset?: any;
}) => {
  const dispatch = useDispatch();
  const { chain } = useNetwork();
  const { openChainModal } = useChainModal();
  const { openConnectModal } = useConnectModal();
  const { address, isConnected } = useAccount();
  const { data: ownedNfts, isLoading } = useGetOwnedNftCounts({
    walletAddress: address,
  });

  const columns: GridColDef[] = [
    {
      field: "image",
      headerName: "Asset",
      flex: 1.5,
      minWidth: 150,
      disableColumnMenu: true,
      disableReorder: true,
      filterable: false,
      sortable: false,
      renderCell: (params: any) => {
        const offer = params.row;
        const collection = offer.collection;
        const item = offer.item;
        const image = getCdnUrl(
          collection?.profilePhotoPath,
          "thumbnail",
          collection?.updatedAt
        );
        let metadata = item.metadata;
        metadata =
          typeof metadata === "string" ? JSON.parse(metadata) : metadata;
        return (
          <div className="flex items-center">
            <img src={image} className="w-10 h-10 rounded-full" />
            <div className="ml-2">
              <p className="text-sm font-bold whitespace-pre-wrap">
                {collection.name}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      field: "issuer",
      headerName: "From",
      flex: 1,
      minWidth: 125,
      disableColumnMenu: true,
      disableReorder: true,
      filterable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div>
            <div className="flex items-center space-x-2">
              <Tooltip title={row?.issuer}>
                <div className="text-sm font-bold truncate">
                  {row?.issuer &&
                    row?.issuer.slice(0, 6) + "..." + row?.issuer.slice(-4)}
                </div>
              </Tooltip>
            </div>
          </div>
        );
      },
    },
    {
      field: "interestRate",
      headerName: "Interest Rate",
      flex: 1,
      minWidth: 125,
      disableColumnMenu: true,
      disableReorder: true,
      filterable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div>
            <div className="flex items-center space-x-2">
              <Typography variant="small">
                {row?.interestRate && row?.interestRate / 100}%
              </Typography>
            </div>
          </div>
        );
      },
    },
    {
      field: "terms",
      headerName: "Terms",
      flex: 1.4,
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      disableColumnMenu: true,
      disableReorder: true,
      filterable: false,
      sortable: false,
      renderCell(params) {
        return (
          <Typography variant="small">
            {params?.row?.borrowAmount &&
              utils.formatEther(params?.row?.borrowAmount)}{" "}
            {params?.row?.paymentTokenName} /{" "}
            {params?.row?.duration &&
              moment().add(params?.row?.duration, "seconds").fromNow(true)}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      // flex: 1,
      minWidth: 150,
      disableColumnMenu: true,
      disableReorder: true,
      filterable: false,
      sortable: false,
      renderCell: ({ row }) => {
        const { issuer, isReady, hasEnoughBalance, isApprovedEnough } = row;
        const isValid = isReady && hasEnoughBalance && isApprovedEnough;
        const { Element, className } = getBalanceAlert(row);

        return (
          <>
            {isConnected && !chain?.unsupported && isValid && (
              <div className="flex items-center w-full px-1.5">
                {!isLoading &&
                  (issuer !== address
                    ? ownedNfts?.[row.nftAddress] > 0
                    : true) && (
                    <button
                      className="bg-accent w-full inline-flex justify-center shadow-accent-volume hover:bg-accent-dark rounded-full py-2 px-1 text-center font-semibold text-white transition-all"
                      onClick={() =>
                        dispatch(
                          setModal({
                            key: "LendingModal",
                            data: {
                              ...row,
                              ...(reset && { reset }),
                              type: issuer === address ? "cancel" : "accept",
                            },
                          })
                        )
                      }
                    >
                      {issuer === address ? "Cancel" : "Accept"}
                    </button>
                  )}
              </div>
            )}
            {isConnected && !isValid && (
              <div className={`w-full flex items-center justify-center`}>
                <div
                  className={`w-fit rounded-lg flex items-center justify-center ${
                    className ?? ""
                  }`}
                >
                  <Element />
                </div>
              </div>
            )}

            {(!isConnected || (isConnected && chain?.unsupported)) && (
              <div className="flex items-center w-full px-1.5">
                <button
                  className="bg-accent w-full inline-flex justify-center shadow-accent-volume hover:bg-accent-dark rounded-full py-2 px-1 text-center font-semibold text-white transition-all"
                  onClick={!isConnected ? openConnectModal : openChainModal}
                >
                  {/* Switch Network */}
                  {!isConnected ? "Connect Wallet" : "Switch Network"}
                </button>
              </div>
            )}
          </>
        );
      },
    },
  ];

  return (
    <DataGrid
      rows={rows}
      loading={loading}
      columns={columns}
      pageSize={pageSize}
      autoHeight
      rowCount={totalCount}
      page={currentPage}
      rowsPerPageOptions={[pageSize]}
      disableSelectionOnClick
      paginationMode="server"
      rowHeight={80}
      componentsProps={{
        noRowsOverlay: {
          className: "dark:bg-jacarta-700 dark:text-white",
        },
        loadingOverlay: {
          className: "dark:bg-jacarta-700 dark:text-white",
        },
      }}
      onPageChange={(e, details) => {
        setCurrentPage(e);
        window.location.href = "#";
      }}
      classes={{
        cell: "dark:text-jacarta-300 text-black ",
        columnHeaderTitle: "dark:text-white text-jacarta-900",
        checkboxInput: "dark:text-jacarta-300 text-black",
        root: "dark:bg-jacarta-700 dark:border-jacarta-600 border-jacarta-100 rounded-2lg border bg-white",
        footerCell: "dark:!text-jacarta-300 !text-black",
        panelFooter: "dark:!text-jacarta-300 !text-black",
        toolbarContainer: "dark:!text-jacarta-300 !text-black",
        toolbarFilterList: "dark:!text-jacarta-300 !text-black",
        footerContainer: "dark:!text-jacarta-300 !text-black",
        filterForm: "dark:!text-jacarta-300 !text-black",
        menuIconButton: "dark:!text-jacarta-300 !text-black",
        sortIcon: "dark:!text-jacarta-300 !text-black",
      }}
      sx={{
        ".MuiToolbar-root": {
          color: "gray",
        },
      }}
    />
  );
};
